export interface AdminCallOrgRef {
  id: number;
  name: string;
  slug: string;
}

/** `id` here is the numeric primary key; `uid` is the same key's UUID the
 *  one used everywhere else in the panel as "the" key id (filtering by
 *  api_key_id, and api_keys[].id on an organization's detail page). The
 *  numeric id still works as a filter too, but uid is what row links use. */
export interface AdminCallKeyRef {
  id: number;
  uid: string;
  name: string;
  prefix: string;
}

/** One row of GET /admin/calls. The backend's serializer ends in `.compact`
 *  request_id and api_key are genuinely absent (not null) on rows where
 *  they don't apply, so both stay optional here rather than `| null`, and
 *  templates must check truthiness, not just render them. */
export class AdminCallRow {
  id!: number;
  endpoint!: string;
  method!: string;
  status!: number;
  error!: boolean;
  credits!: number;
  costCents!: number;
  /** Charges for paid options, already folded into costCents show both:
   *  "3¢ (2¢ options)" per the contract, not cost_cents + surcharge_cents. */
  surchargeCents!: number;
  latencyMs!: number;
  source!: string;
  requestId?: string;
  organization!: AdminCallOrgRef;
  apiKey?: AdminCallKeyRef;
  createdAt!: string;

  constructor(data: any = {}) {
    this.id = data.id;
    this.endpoint = data.endpoint;
    this.method = data.method;
    this.status = data.status;
    this.error = !!data.error;
    this.credits = data.credits;
    this.costCents = data.cost_cents;
    this.surchargeCents = data.surcharge_cents;
    this.latencyMs = data.latency_ms;
    this.source = data.source;
    this.requestId = data.request_id;
    this.organization = data.organization;
    this.apiKey = data.api_key;
    this.createdAt = data.created_at;
  }
}

/** `totals` on GET /admin/calls, for the whole filtered list (every filter
 *  the rows honour). Same fields as /admin/overview's `usage`. */
export class AdminCallTotals {
  requests!: number;
  errors!: number;
  credits!: number;
  costCents!: number;
  avgLatencyMs!: number;
  bySource: Record<string, number> = {};
  currency!: string;

  constructor(data: any = {}) {
    this.requests = data.requests ?? 0;
    this.errors = data.errors ?? 0;
    this.credits = data.credits ?? 0;
    this.costCents = data.cost_cents ?? 0;
    this.avgLatencyMs = data.avg_latency_ms ?? 0;
    this.bySource = data.by_source ?? {};
    this.currency = data.currency ?? 'eur';
  }

  /** 0–1. */
  get errorRate(): number {
    return this.requests > 0 ? this.errors / this.requests : 0;
  }
}
